<template>
  <div class="menu">
    <div class="left">
      <ul
        class="links-list"
        :class="
          !isMobileOrTablet &&
          headerData.hasSearchBar &&
          (searchBoxIsBox || searchBoxFullWidth)
            ? '!w-0'
            : 'w-full'
        "
      >
        <li
          v-if="headerData.showCategories"
          v-click-outside="closeExperiencesMenu"
          class="menu-item"
        >
          <button
            class="menu-button"
            :style="navTextColorStyle"
            @click="experiencesMenuStatus = !experiencesMenuStatus"
          >
            {{ t(headerData.categories.title) }}
            <icon-chevron-black class="chevron mx-3" />
          </button>
          <TheHeaderExperiencesList
            :is-open="experiencesMenuStatus"
            @close="closeExperiencesMenu"
          />
        </li>
        <li class="menu-item" v-if="elfsightForm">
          <button
            class="menu-button"
            :style="navTextColorStyle"
            @click="emit('open-elfsight-form')"
          >
            {{ t("form.title") }}
          </button>
        </li>
        <li
          v-for="(link, index) in headerlinksCheck"
          :key="index"
          class="menu-item"
        >
          <NuxtLink
            :to="link.itsOuterLink ? link.target : localePath(link.target)"
            :target="link.itsOuterLink ? '_blank' : '_self'"
            class="menu-button"
            :style="navTextColorStyle"
          >
            {{ t(link?.title) }}
          </NuxtLink>
        </li>
        <li
          v-for="(dropdownMenu, index) in headerData.dropdownMenus"
          :key="`dropdown-${index}`"
          class="menu-item max-lg:!py-0"
        >
          <TheHeaderLinksDropdown
            :title="dropdownMenu.title"
            :links="dropdownMenu.items"
            class="menu-button"
            :style="navTextColorStyle"
          />
        </li>
        <template v-if="headerData.links.length > maxVisibleLinks">
          <li v-if="!isMobileOrTablet" class="menu-item max-lg:!py-0">
            <TheHeaderLinksDropdown
              :links="headerData.links.slice(maxVisibleLinks - 1)"
              class="menu-button"
            />
          </li>
          <template v-else>
            <li
              v-for="link in headerData.links.slice(maxVisibleLinks - 1)"
              :key="link.title"
              class="menu-item"
            >
              <NuxtLink
                :to="link.itsOuterLink ? link.target : localePath(link.target)"
                :target="link.itsOuterLink ? '_blank' : '_self'"
                class="menu-button"
                :style="navTextColorStyle"
              >
                {{ t(link?.title) }}
              </NuxtLink>
            </li>
          </template>
        </template>
      </ul>
      <template v-if="headerData.searchBarType == 'supplier'">
        <SearchBoxTwoSupplier
          v-show="headerData.hasSearchBar ? true : false"
          :is-box="searchBoxStatus"
          :class="searchBoxFullWidth ? 'active' : 'w-6'"
          class="main-search max-lg:hidden"
          @update-full-width-status="searchBoxFullWidth = $event"
        />
      </template>
      <template v-else>
        <SearchBoxOneFour
          v-show="headerData.hasSearchBar ? true : false"
          :is-box="searchBoxStatus"
          :top-cities="topCities"
          :class="searchBoxFullWidth ? 'active' : 'w-6'"
          class="main-search max-lg:hidden"
          @update-full-width-status="searchBoxFullWidth = $event"
        />
      </template>
    </div>
    <div class="right-menu">
      <Cart
        v-if="!isMobileOrTablet && isSupplier"
        :is-scrolled="isScrolled"
        @open-login="emit('open-login', $event)"
      />

      <TheHeaderSwitcher
        v-if="headerData.isMultiLanguage && checkMultiLang"
        :active-item="getActiveLang"
        :show-code="false"
        :list="availableLocales"
        :header-data="headerData"
        is-centered
        :route-path="routePath"
        :is-scrolled="isScrolled"
        @update:active-item="switchLanguage"
      />

      <TheHeaderSwitcher
        v-if="headerData.isMultiCurrency && checkMultiCurrency"
        :list="simplifycurrencies(currenciesList)"
        :active-item="getActiveCurrency"
        :header-data="headerData"
        :route-path="routePath"
        :is-scrolled="isScrolled"
        @update:active-item="switchCurrency"
      />

      <Cart
        v-if="!isMobileOrTablet && !isSupplier"
        :is-scrolled="isScrolled"
        @open-login="emit('open-login', $event)"
      />

      <NuxtLink
        v-if="config.public.header?.actionButton"
        class="phone-call whitespace-nowrap max-md:mt-4"
        :to="config.public.header?.actionButton?.target"
        target="_blank"
      >
        {{ config.public.header.actionButton.label }}
      </NuxtLink>
      <a
        v-if="isSupplier && headerData.phone"
        :tel="headerData.phone"
        :href="`tel:${headerData.phone}`"
        class="phone-call max-md:mt-3"
      >
        <icon-phone-in-call />
        <span dir="ltr"> {{ headerData.phone }} </span>
      </a>

      <div v-if="$te('header.license')" class="header-license">
        {{ t("header.license") }}
        <b class="text-mobile-heading-6 md:text-heading-6 !font-bold">{{
          t("header.licenseCode")
        }}</b>
      </div>

      <div class="md:hidden absolute bottom-10">
        <LayoutTheFooterSocialMedias
          :socials="config.public.footer.socialLinks"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, type PropType, ref } from "vue";
import { useCookie } from "nuxt/app";
import type { ICurrency, ITopCities } from "../../../types/general";
import TheHeaderSwitcher from "./TheHeaderSwitcher.vue";
import TheHeaderLinksDropdown from "./TheHeaderLinksDropdown.vue";
import TheHeaderExperiencesList from "./TheHeaderExperiencesList.vue";
import SearchBoxOneFour from "../../search-box/SearchBoxOneFour.vue";
import SearchBoxTwoSupplier from "../../search-box/SearchBoxTwoSupplier.vue";
import { useOneTap, type CredentialResponse } from "vue3-google-signin";

const { t, locales, setLocale } = useI18n();
const config: any = useRuntimeConfig();
const localePath = useLocalePath();
const { isLoggedIn } = useAuth();

const emit = defineEmits(["open-login", "open-elfsight-form"]);
const props = defineProps({
  headerData: {
    type: Object,
    default: () => {
      return {};
    },
  },
  searchBoxIsBox: { type: Boolean, default: false },
  isSupplier: { type: Boolean, default: false },
  routePath: { type: String, default: "/" },
  currenciesList: { type: Array as PropType<ICurrency[]>, required: true },
  topCities: { type: Array as PropType<ITopCities[]>, required: true },
  elfsightForm: { type: String as PropType<string | null>, default: null },
  isScrolled: { type: Boolean, default: false },
});

const isTemplate4 = props.headerData?.type === "template4";
const isSupplier4 = props.headerData?.type === "supplier4";

const navTextColorStyle = computed(() => {
  return props.headerData?.navTextColor && !props.isScrolled
    ? { color: `var(--${props.headerData.navTextColor})` }
    : undefined;
});

const maxVisibleLinks = props.elfsightForm
  ? 4
  : props.headerData.desktopMaxVisibleLinks ?? 5;
const { locale } = useI18n();
const activeLang = ref(locale.value);
const activeCurrency = useCookie<string>("currency");
const windowWidth = ref<number>(0);
const experiencesMenuStatus = ref<boolean>(false);

const closeExperiencesMenu = () => {
  if (experiencesMenuStatus.value) {
    experiencesMenuStatus.value = false;
  }
};

const headerlinksCheck = computed(() => {
  return props.headerData.links?.length > maxVisibleLinks
    ? props.headerData.links?.slice(0, maxVisibleLinks - 1)
    : props.headerData?.links;
});

const checkMultiLang = computed(() => {
  return locales.value && locales.value.length > 1;
});

const checkMultiCurrency = computed(() => {
  return props.currenciesList && props.currenciesList.length > 1;
});

const getActiveLang = computed(() => {
  return activeLang.value || "";
});

const getActiveCurrency = computed(() => {
  return activeCurrency.value || "";
});

const availableLocales = computed(() => {
  return locales.value;
});

const isMobileOrTablet = computed(() => {
  return windowWidth.value > 0 && windowWidth.value < 1024;
});

const searchBoxStatus = computed(() => {
  return isMobileOrTablet.value ? true : props.searchBoxIsBox;
});
const searchBoxFullWidth = ref<boolean>(searchBoxStatus.value);

const switchLanguage = async (langCode: string) => {
  if (langCode !== locale.value) {
    activeLang.value = langCode;
    await setLocale(langCode);
  }
};

const switchCurrency = (newCurrencyCode: string) => {
  if (newCurrencyCode !== activeCurrency.value) {
    activeCurrency.value = newCurrencyCode;
    window.location.reload();
  }
};

const simplifycurrencies = (original: ICurrency[] | null) => {
  const simplified = original?.map((item) => ({
    name: item?.title,
    code: item.currency_code,
    id: item.pivot.currency_id,
  }));

  return simplified || [];
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  windowWidth.value = window.innerWidth;
  window.addEventListener("resize", handleResize);

  if (!isLoggedIn) {
    useOneTap({
      onSuccess: (response: CredentialResponse) => {
        console.log("Success:", response);
      },
      onError: () => console.error("Error with One Tap Login"),
      // options
    });
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
